/**
 * useHosts — Story 2.2 Host-Management-UI (Datenhooks)
 *
 * Referenz (AUTORITATIV): claude-design-import/hosts.html.
 *
 * Datenquellen (ausschliesslich generierter api-client + api-types, Welle 2):
 *  - GET /api/v2/hosts               → Host-Stammdaten (paginiert)
 *  - GET /api/v2/hosts/{id}          → Host-Detail (Deep-Link /hosts/:id)
 *  - GET /api/v2/host_split_rules    → Zuordnung Host ↔ Hotspot (Regeln)
 *  - GET /api/v2/settlements         → Auszahlungs-Status + Monats-Ertrag/GB
 *  - GET /api/v2/hotspots            → Namensaufloesung der Hotspot-IDs
 *
 * Dokumentierte Grenze: Die Anreicherungs-Endpunkte (Regeln, Abrechnungen,
 * Hotspots) werden mit per_page=100 (Kontrakt-Maximum) auf Seite 1 geladen.
 * Ein Server-Aggregat "Hosts inkl. Hotspots/Saldo" existiert im Contract
 * nicht — bei mehr als 100 Regeln/Abrechnungen/Hotspots fehlen einzelne
 * Chips/Statuswerte (siehe open_issue der Story). Es wird NICHTS erfunden:
 * fehlende Werte rendern als noValue-Platzhalter.
 */
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'
import type { components } from '@heliumtracker/api-types'

export type Host = components['schemas']['Host']
export type Settlement = components['schemas']['Settlement']
export type SettlementTotal = components['schemas']['SettlementTotal']
export type HostSplitRule = components['schemas']['HostSplitRule']
export type PagyMeta = components['schemas']['PagyMeta']

export type SettlementStatus = NonNullable<Settlement['status']>

/** Kontrakt-Maximum fuer per_page (siehe openapi.yaml). */
const MAX_PER_PAGE = 100

// --- Host-Liste (paginiert) --------------------------------------------------

type HostListResponse = {
  data?: Host[]
  meta?: { pagy?: PagyMeta }
}

export function useHosts(page = 1) {
  const query = useApi<HostListResponse>(['hosts', page], () =>
    apiClient.GET('/api/v2/hosts', {
      params: { query: { page, per_page: 25 } },
    }),
  )

  return {
    hosts: query.data?.data,
    pagy: query.data?.meta?.pagy,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}

// --- Host-Detail (Deep-Link /hosts/:id) --------------------------------------

type HostDetailResponse = { data?: Host }

export function useHost(id: number | undefined) {
  const query = useApi<HostDetailResponse>(
    ['host-detail', id],
    () =>
      apiClient.GET('/api/v2/hosts/{id}', {
        params: { path: { id: id as number } },
      }),
    { enabled: id !== undefined && Number.isFinite(id) },
  )

  return {
    host: query.data?.data,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}

// --- Provisions-Regeln (Hotspot-Zuordnung je Host) ---------------------------

type SplitRuleListResponse = {
  data?: HostSplitRule[]
  meta?: { pagy?: PagyMeta }
}

export function useHostSplitRules() {
  const query = useApi<SplitRuleListResponse>(['hosts', 'split-rules'], () =>
    apiClient.GET('/api/v2/host_split_rules', {
      params: { query: { per_page: MAX_PER_PAGE } },
    }),
  )

  const rules = query.data?.data
  const pagy = query.data?.meta?.pagy

  return {
    rules,
    /** Echte Gesamtzahl aus meta.pagy (nicht die Laenge der geladenen Seite). */
    ruleCount: pagy?.count,
    /** true, wenn mehr Regeln existieren als geladen wurden (per_page=100). */
    rulesTruncated: pagy !== undefined && pagy.count > (rules?.length ?? 0),
    isLoading: query.isLoading,
  }
}

// --- Abrechnungen (Auszahlungs-Status + Monats-Ertrag/GB) --------------------

type SettlementListResponse = components['schemas']['SettlementListResponse']

export function useHostsSettlements() {
  const query = useApi<SettlementListResponse>(['hosts', 'settlements'], () =>
    apiClient.GET('/api/v2/settlements', {
      params: { query: { per_page: MAX_PER_PAGE } },
    }),
  )

  const settlements = query.data?.data
  const pagy = query.data?.meta?.pagy

  return {
    settlements,
    /** Globale Summen je Waehrung/Status (Server-Aggregat, nicht gekappt). */
    totals: query.data?.meta?.totals,
    /**
     * true, wenn die Abrechnungsliste bei per_page=100 gekappt ist. Die aus
     * `settlements` abgeleiteten ZAEHLER sind dann Untergrenzen — die Betraege
     * aus `totals` bleiben global. Das UI muss diesen Mix kenntlich machen.
     */
    settlementsTruncated: pagy !== undefined && pagy.count > (settlements?.length ?? 0),
    isLoading: query.isLoading,
  }
}

// --- Hotspot-Namen (fuer die Chips in der Liste) -----------------------------

type HotspotListResponse = {
  data: components['schemas']['Hotspot'][]
  meta?: { pagy?: PagyMeta }
}

export function useHotspotNameMap(): Map<string, string> {
  const query = useApi<HotspotListResponse>(['hosts', 'hotspot-names'], () =>
    apiClient.GET('/api/v2/hotspots', {
      params: { query: { per_page: MAX_PER_PAGE } },
    }),
  )

  const map = new Map<string, string>()
  for (const h of query.data?.data ?? []) {
    if (h.id !== undefined && h.name) map.set(String(h.id), h.name)
  }
  return map
}

// --- Anreicherung je Host ----------------------------------------------------

export type HostRowData = {
  /** Hotspot-IDs aus den Provisions-Regeln dieses Hosts (dedupliziert). */
  hotspotIds: number[]
  /** Anzahl Provisions-Regeln dieses Hosts. */
  ruleCount: number
  /** Juengste Abrechnung (hoechster period_month) — oder undefined. */
  latestSettlement: Settlement | undefined
}

/**
 * Gruppiert Regeln + Abrechnungen client-seitig je Host.
 * `latestSettlement` ist die Abrechnung mit dem lexikografisch groessten
 * period_month (Format YYYY-MM sortiert korrekt als String).
 */
export function buildHostRowData(
  rules: HostSplitRule[] | undefined,
  settlements: Settlement[] | undefined,
): Map<number, HostRowData> {
  const map = new Map<number, HostRowData>()

  const entry = (hostId: number): HostRowData => {
    let e = map.get(hostId)
    if (!e) {
      e = { hotspotIds: [], ruleCount: 0, latestSettlement: undefined }
      map.set(hostId, e)
    }
    return e
  }

  for (const rule of rules ?? []) {
    if (rule.host_id === undefined) continue
    const e = entry(rule.host_id)
    e.ruleCount += 1
    if (rule.hotspot_id !== undefined && !e.hotspotIds.includes(rule.hotspot_id)) {
      e.hotspotIds.push(rule.hotspot_id)
    }
  }

  for (const s of settlements ?? []) {
    if (s.host_id === undefined) continue
    const e = entry(s.host_id)
    const current = e.latestSettlement
    if (!current || (s.period_month ?? '') > (current.period_month ?? '')) {
      e.latestSettlement = s
    }
  }

  return map
}
